import LOCAL_STORAGE_KEY from '../constants/localStorageKey';
import { DEEPLINK_PARAMS } from '../constants/deeplinkParams';
import { getStorage } from '../utils/general.util';

// Referral attribution window: a capture older than this is ignored at
// attribution time (MainLayout). Product-locked at 30 days.
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const REFERRAL_TTL_SECONDS = REFERRAL_TTL_MS / 1000;

// First-party cookie backup for the capture. localStorage is the primary
// store, but a cookie survives cases where storage is cleared/partitioned
// between the landing and the sign-up (e.g. the /login bounce on some
// in-app webviews). Same key namespace, same TTL.
const REFERRAL_COOKIE = 'heru_referral';

// Persisted capture shape. Legacy captures were a bare code string under the
// same localStorage key — `readReferralCapture` migrates those on read.
export type ReferralCapture = {
	code: string;
	campaignId?: number;
	capturedAt: number;
};

const writeCookie = (value: string): void => {
	try {
		document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(value)}; max-age=${REFERRAL_TTL_SECONDS}; path=/; SameSite=Lax`;
	} catch {
		// cookies unavailable — localStorage already holds the capture
	}
};

const readCookie = (): string | null => {
	try {
		const match = document.cookie
			.split('; ')
			.find((row) => row.startsWith(`${REFERRAL_COOKIE}=`));
		return match ? decodeURIComponent(match.split('=')[1]) : null;
	} catch {
		return null;
	}
};

// Single writer for a referral capture. Used by the boot URL capture and the
// `/r/:code` short-link route — both funnel here so the storage format stays
// in one place.
export const persistReferralCapture = (
	code: string,
	campaignId?: number,
): void => {
	const trimmed = code.trim();
	if (!trimmed) return;

	const capture: ReferralCapture = {
		code: trimmed,
		...(campaignId != null && Number.isFinite(campaignId) ? { campaignId } : {}),
		capturedAt: Date.now(),
	};
	const serialized = JSON.stringify(capture);

	getStorage().setItem(LOCAL_STORAGE_KEY.REFERRAL_CODE, serialized);
	writeCookie(serialized);
};

const parseCapture = (raw: string): ReferralCapture | null => {
	// New format: JSON `{ code, campaignId?, capturedAt }`.
	try {
		const parsed = JSON.parse(raw);
		if (
			parsed &&
			typeof parsed === 'object' &&
			typeof parsed.code === 'string'
		) {
			return parsed as ReferralCapture;
		}
	} catch {
		// not JSON — fall through to the legacy plain-string branch
	}
	// Legacy format: a bare code string with no timestamp. Treat as valid and
	// let the caller migrate it to the timestamped format.
	if (raw.trim()) {
		return { code: raw.trim(), capturedAt: Date.now() };
	}
	return null;
};

export const clearReferralCapture = (): void => {
	getStorage().removeItem(LOCAL_STORAGE_KEY.REFERRAL_CODE);
	// Legacy sibling key — clear it too so a stale campaign id can't resurface.
	getStorage().removeItem(LOCAL_STORAGE_KEY.REFERRAL_CAMPAIGN_ID);
	try {
		document.cookie = `${REFERRAL_COOKIE}=; max-age=0; path=/; SameSite=Lax`;
	} catch {
		// nothing to clear
	}
};

// Lectura PURA del código capturado, para instrumentación.
//
// `readReferralCapture` NO es pura: rehidrata el storage cuando la captura vino
// de la cookie o del formato legacy, y `persistReferralCapture` sella
// `capturedAt: Date.now()` — o sea que leerla ESTIRA la ventana de atribución
// de 30 días. También borra la captura cuando ya caducó. Ninguna de las dos
// cosas puede pasar por observar: un evento de analítica no debe mover el
// comportamiento del producto que está midiendo.
//
// Misma semántica de ventana, sin escribir nada. Las capturas legacy quedan
// incluidas igual que en el lector normal, porque `parseCapture` les sella
// `capturedAt` en «ahora» y por definición caen dentro de la ventana.
export const peekReferralCode = (): string | null => {
	const stored = getStorage().getItem(LOCAL_STORAGE_KEY.REFERRAL_CODE);
	const capture = stored
		? parseCapture(stored)
		: parseCapture(readCookie() ?? '');

	if (!capture) return null;
	if (Date.now() - capture.capturedAt > REFERRAL_TTL_MS) return null;
	return capture.code;
};

// Reads the active referral capture, or null if there is none or it's past
// the 30-day window. Migrates a legacy plain-string value to the new format.
// Falls back to the cookie backup when localStorage lost the capture.
export const readReferralCapture = (): ReferralCapture | null => {
	const storage = getStorage();
	const stored = storage.getItem(LOCAL_STORAGE_KEY.REFERRAL_CODE);

	let capture = stored ? parseCapture(stored) : null;
	let fromCookie = false;

	if (!capture) {
		const cookie = readCookie();
		capture = cookie ? parseCapture(cookie) : null;
		fromCookie = capture != null;
	}

	if (!capture) return null;

	// A legacy value is a bare string equal to the code (parseCapture stamped
	// `capturedAt` to "now"), so it never expires on this read — grandfathered
	// in, then migrated below.
	const isLegacy = stored != null && stored === capture.code;
	if (!isLegacy && Date.now() - capture.capturedAt > REFERRAL_TTL_MS) {
		clearReferralCapture();
		return null;
	}

	// Rehydrate the primary store from a legacy value or a cookie-only capture.
	if (isLegacy || fromCookie) {
		persistReferralCapture(capture.code, capture.campaignId);
	}

	return capture;
};

// Persists a referral invitation (`?referral_code` + `?campaign_id`) at boot
// so it survives the /login bounce and a later sign-up. MainLayout attributes
// it once the user profile loads (createReferral). Called once in main.tsx,
// before React mounts — same pattern as the UTM and checkout-intent captures.
export const captureReferralFromUrl = (): void => {
	const params = new URLSearchParams(window.location.search);
	const referralCode = params.get(DEEPLINK_PARAMS.REFERRAL_CODE);
	const campaignIdRaw = params.get(DEEPLINK_PARAMS.CAMPAIGN_ID);
	if (!referralCode) return;

	const campaignId = campaignIdRaw ? Number(campaignIdRaw) : undefined;
	persistReferralCapture(referralCode, campaignId);
};
